import { useEffect, useId, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  ButtonLink,
  Card,
  CardBody,
  CardHeader,
  Container,
  Text,
} from '@backstage/ui';
import { BreadcrumbEntry, useApi } from '@backstage/frontend-plugin-api';
import { MarkdownContent } from '@backstage/core-components';
import {
  scaffolderApiRef,
  useTaskEventStream,
  type ScaffolderTaskOutput,
} from '@backstage/plugin-scaffolder-react';
import {
  DefaultTemplateOutputs,
  TaskLogStream,
} from '@backstage/plugin-scaffolder-react/alpha';
import { CancelTaskDialog } from './CancelTaskDialog';
import { DeletionSteps } from './DeletionSteps';
import { stepPhases, type StepView } from './deletionTask';
import {
  envResultTitle,
  envRowGroups,
  pageOf,
  readEnvReport,
  type EnvReportData,
} from './environmentTask';
import { useTaskTemplate } from './taskTemplate';
import { useProjectLifecycle } from '../shared/templateRoles';
import { waadooIcons } from '../ui/icons/waadooIcons';
import { CardTitle } from '../ui';

/** The right column while the task runs. */
function RunningCard(props: { running?: StepView; cancelRequested: boolean }) {
  const { running, cancelRequested } = props;
  return (
    <Card>
      <CardHeader>
        <CardTitle
          level={3}
          title={
            running ? `En cours : ${running.name}` : 'En attente du démarrage'
          }
        />
      </CardHeader>
      <CardBody>
        <Text as="p" variant="body-medium" color="secondary">
          {cancelRequested
            ? 'Annulation demandée : aucune autre étape ne sera lancée.'
            : 'Le compte rendu s’affichera ici à la fin. Le journal détaillé reste accessible avec le bouton « Journal ».'}
        </Text>
      </CardBody>
    </Card>
  );
}

/** One line of the report's inline markdown (`code`, **bold**). */
function InlineMarkdown(props: { text: string }) {
  return <MarkdownContent className="waadoo-md-inline" content={props.text} />;
}

/**
 * The final report of a wake, a sleep or a promotion (`reportData` of its
 * last step): the result, what needs a look, what was done by kind — the
 * deletion report's groups and chips —, the merge request of a promotion,
 * the address after a wake or a promotion, « À savoir »; the template's
 * markdown outputs folded at the end.
 */
function EnvironmentReport(props: {
  report: EnvReportData;
  output: ScaffolderTaskOutput;
  /** The application's name (`taskTemplate.ts`). */
  appTitle: string;
}) {
  const { report, output, appTitle } = props;
  const details = output.text ?? [];
  return (
    <div className="waadoo-deletion-report">
      <Alert
        status={report.outcome === 'ok' ? 'success' : 'warning'}
        icon
        title={envResultTitle(report)}
        role="status"
      />
      <Card>
        <CardHeader>
          <CardTitle
            level={3}
            title={
              <>
                Compte rendu — {report.project}/{report.environment}
              </>
            }
          />
        </CardHeader>
        <CardBody>
          <div className="waadoo-deletion-report__body">
            {report.warnings.map((warning, index) => (
              <Alert
                key={index}
                status="warning"
                icon
                title="Attention"
                description={<InlineMarkdown text={warning} />}
              />
            ))}
            <section>
              <Text as="h4" variant="body-large" weight="bold">
                Ce qui a été fait
              </Text>
              <div className="waadoo-deletion-report__groups">
                {envRowGroups(report).map(group => (
                  <section
                    key={group.kind}
                    className="waadoo-deletion-report__group"
                    aria-label={group.title}
                  >
                    <div className="waadoo-deletion-report__group-header">
                      <span
                        className="waadoo-deletion-report__pill"
                        data-tone={group.tone}
                      >
                        {group.title}
                      </span>
                    </div>
                    <ul className="waadoo-deletion-report__rows">
                      {group.rows.map(row => (
                        <li
                          key={row.element}
                          className="waadoo-deletion-report__row"
                        >
                          <span aria-hidden />
                          <span className="waadoo-deletion-report__row-element">
                            {row.element}
                          </span>
                          <span className="waadoo-deletion-report__chips">
                            {row.items.map(item => (
                              <span
                                key={item}
                                className="waadoo-deletion-report__chip"
                              >
                                {item}
                              </span>
                            ))}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            </section>
            {report.mergeRequestUrl && (
              <section className="waadoo-deletion-report__next">
                <div>
                  <Text as="h4" variant="body-medium" weight="bold">
                    Merge request
                  </Text>
                  <Text as="p" variant="body-small" color="secondary">
                    {report.mergeRequestUrl}
                  </Text>
                </div>
                <ButtonLink
                  variant="secondary"
                  href={report.mergeRequestUrl}
                  target="_blank"
                  rel="noopener"
                >
                  Voir la merge request
                </ButtonLink>
              </section>
            )}
            {report.url && (
              <section className="waadoo-deletion-report__next">
                <div>
                  <Text as="h4" variant="body-medium" weight="bold">
                    Adresse
                  </Text>
                  <Text as="p" variant="body-small" color="secondary">
                    {report.url}
                  </Text>
                </div>
                <ButtonLink
                  variant="primary"
                  href={report.url}
                  target="_blank"
                  rel="noopener"
                >
                  Ouvrir {appTitle}
                </ButtonLink>
              </section>
            )}
            {report.notes.length > 0 && (
              <section>
                <Text as="h4" variant="body-large" weight="bold">
                  À savoir
                </Text>
                <ul className="waadoo-deletion-report__notes">
                  {report.notes.map((note, index) => (
                    <li key={index}>
                      <InlineMarkdown text={note} />
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {details.length > 1 && (
              <details className="waadoo-deletion-report__details">
                <summary>Détail des étapes</summary>
                <DefaultTemplateOutputs output={{ text: details.slice(1) }} />
              </details>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * One run of the task page: its own event stream. Remounted by
 * `EnvironmentTaskPage` after « Reprendre », so that the stream of the
 * retried task is read from its start.
 */
function EnvironmentTaskRun(props: { taskId: string; onRetried: () => void }) {
  const { taskId, onRetried } = props;
  const stream = useTaskEventStream(taskId);
  const scaffolderApi = useApi(scaffolderApiRef);
  const [logsOpen, setLogsOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelRequested, setCancelRequested] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryFailure, setRetryFailure] = useState<string>();
  const logsId = useId();

  useEffect(() => {
    if (stream.error) setLogsOpen(true);
  }, [stream.error]);

  const { task } = stream;
  const template = useTaskTemplate(task);
  // The steps' texts by the project's type (point 8): profile, backups.
  const lifecycle = useProjectLifecycle(template.appType);
  const page = pageOf(task?.spec.templateInfo?.entityRef, {
    ...template,
    lifecycle,
    // A restore's steps by its operation (restore, validate, cancel).
    operation: task?.spec.parameters.operation,
  });
  const layout = page.layout;
  const phases = useMemo(
    () => (task ? stepPhases(task, stream.steps, layout) : []),
    [task, stream.steps, layout],
  );

  if (!task) {
    return (
      <Container>
        {stream.error ? (
          <Alert
            mt="4"
            status="danger"
            icon
            title="La tâche n’a pas pu être lue"
            description={stream.error.message}
          />
        ) : (
          <Text as="p" variant="body-medium" color="secondary">
            Chargement de la tâche…
          </Text>
        )}
      </Container>
    );
  }

  const steps = phases.flatMap(phase => phase.steps);
  const running = steps.find(step => step.state === 'running');
  const failed = steps.find(step => step.state === 'failed');
  const finished = stream.completed;
  const cancellable = !finished && !stream.cancelled && !cancelRequested;
  const report =
    finished && !stream.error ? readEnvReport(stream.output) : undefined;
  const title = task.spec.templateInfo?.entity?.metadata.title ?? page.title;

  // « Reprendre »: the scaffolder runs the task again; the steps already
  // done find their work done and do not repeat it (backend
  // `environmentPower/`).
  const retry = async () => {
    setRetrying(true);
    setRetryFailure(undefined);
    try {
      await scaffolderApi.retry(taskId);
      onRetried();
    } catch (error: unknown) {
      setRetryFailure(error instanceof Error ? error.message : String(error));
      setRetrying(false);
    }
  };

  let main;
  if (stream.error) {
    main = (
      <Alert
        status="danger"
        icon
        role="alert"
        title={
          failed ? `Échec à l’étape « ${failed.name} »` : 'La tâche a échoué'
        }
        description={<MarkdownContent content={stream.error.message} />}
      />
    );
  } else if (!finished) {
    main = (
      <RunningCard
        running={running}
        cancelRequested={cancelRequested || stream.cancelled}
      />
    );
  } else if (report) {
    main = (
      <EnvironmentReport
        report={report}
        output={stream.output ?? {}}
        appTitle={template.appTitle}
      />
    );
  } else {
    main = <DefaultTemplateOutputs output={stream.output} />;
  }

  return (
    <BreadcrumbEntry entry={{ label: taskId, href: taskId }}>
      <Container>
        <div className="waadoo-deletion-task">
          <Text as="h2" variant="title-medium">
            {title}
          </Text>
          <div className="waadoo-deletion-task__layout">
            <Card className="waadoo-deletion-task__steps">
              <CardHeader>
                <CardTitle level={3} title="Étapes" />
              </CardHeader>
              <CardBody>
                <DeletionSteps phases={phases} />
                <div className="waadoo-deletion-task__actions">
                  {cancellable && (
                    <Button
                      variant="secondary"
                      onPress={() => setCancelOpen(true)}
                    >
                      Annuler
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    aria-expanded={logsOpen}
                    aria-controls={logsId}
                    onPress={() => setLogsOpen(open => !open)}
                  >
                    Journal
                  </Button>
                  {finished && stream.error && (
                    <Button
                      variant="primary"
                      isPending={retrying}
                      onPress={() => void retry()}
                    >
                      Reprendre
                    </Button>
                  )}
                </div>
                {retryFailure && (
                  <Text variant="body-small" color="danger" role="alert">
                    La reprise a échoué : {retryFailure}
                  </Text>
                )}
              </CardBody>
            </Card>
            <div className="waadoo-deletion-task__main">{main}</div>
          </div>
          <div id={logsId} hidden={!logsOpen}>
            {logsOpen && (
              <Card>
                <CardHeader>
                  <CardTitle
                    level={3}
                    icon={waadooIcons.logs}
                    title="Journal"
                  />
                </CardHeader>
                <CardBody>
                  <div className="waadoo-deletion-task__log">
                    <TaskLogStream logs={stream.stepLogs} />
                  </div>
                </CardBody>
              </Card>
            )}
          </div>
        </div>
        <CancelTaskDialog
          taskId={taskId}
          description="Les étapes suivantes ne seront pas lancées. Ce qui a déjà été fait n’est pas défait : « Reprendre » relance la tâche là où elle s’est arrêtée."
          isOpen={cancelOpen}
          onOpenChange={setCancelOpen}
          onCancelled={() => setCancelRequested(true)}
        />
      </Container>
    </BreadcrumbEntry>
  );
}

/**
 * The task page of the environment templates (`env-wake`, `env-sleep`,
 * `env-promote`, `env-configure`, another type's), in place of the
 * scaffolder's (`plugin.tsx` chooses), in the project deletion page's
 * design: left, the steps in phases (`DeletionSteps`), « Annuler » while it
 * runs, « Journal », « Reprendre » after a failure (the scaffolder's retry:
 * the steps already done are recognised and not repeated); right, what
 * runs, then the report (`reportData` of the last step), or the failed step
 * and its error.
 */
export function EnvironmentTaskPage(props: { taskId: string }) {
  const [run, setRun] = useState(0);
  return (
    <EnvironmentTaskRun
      key={run}
      taskId={props.taskId}
      onRetried={() => setRun(current => current + 1)}
    />
  );
}
